import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import useRefCallback from '../../../../utils/react/useRefCallback';
import { isHotInstanceAvailable } from '../../bridge/utils/hotInstanceUtils/hotTableInstanceValidationUtils';
import { useDispatch } from 'react-redux';
import { removeSelectedElements, startEditingElement } from '../../../selection/selectionActions';
import {
    getTableCellEditorId,
    getTableEditorId,
    getTableGridEditorId,
} from '../../bridge/utils/tableEditorSessionUtils';
import {
    TABLE_CELL_EDITOR_KEY,
    TABLE_GRID_EDITOR_KEY,
    TABLE_TITLE_EDITOR_KEY,
} from '../../../../../common/table/tableConstants';
import { CAPTION_EDITOR_KEY } from '../../../../components/caption/captionConstants';
import { getSpecificEditorId } from '../../../utils/elementEditorUtils';
import { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { CellEditingSession } from '../../../../../common/table/TableTypes';
import type { Rect } from '../../../../../common/maths/geometry/rect/rectTypes';

/**
 * Hooks used by the Milanote table editing plugins (MilanoteEditingPlugin & MilanoteCellEditor).
 *
 * NOTE: `useStableCallback` allows us to pass these functions into the HotTable config (which is only
 * initialised once) without worrying about the function identity changing on each render.
 */
export const useTableEditingHandlers = ({
    hotTableInstanceRef,
    showTitle,
    showCaption,
    elementId,
    isCurrentlyEditingTableCell,
    setCellEditingSession,
}: {
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    showTitle: boolean;
    showCaption: boolean;
    elementId: string;
    isCurrentlyEditingTableCell: boolean;
    setCellEditingSession: Dispatch<SetStateAction<CellEditingSession | null>>;
}) => {
    const dispatch = useDispatch();

    const dispatchStartEditingTitle = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_TITLE_EDITOR_KEY,
                    editorId: getTableEditorId(elementId),
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    const dispatchStartEditingCaption = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: CAPTION_EDITOR_KEY,
                    editorId: getSpecificEditorId(elementId, CAPTION_EDITOR_KEY),
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    const dispatchStartEditingTableCell = useCallback(
        ({ editorFocusRect, preventVirtualKeyboard = false }) =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_CELL_EDITOR_KEY,
                    editorId: getTableCellEditorId(elementId),
                    editorFocusRect,
                    elementId: undefined,
                    preventVirtualKeyboard,
                }),
            ),
        [elementId],
    );

    const dispatchStartEditingTableGrid = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_GRID_EDITOR_KEY,
                    editorId: getTableGridEditorId(elementId),
                    elementId: undefined,
                    preventVirtualKeyboard: true,
                }),
            ),
        [elementId],
    );

    /**
     * Deselects the table element.
     */
    const deselectElement = useRefCallback(() => {
        dispatch(removeSelectedElements({ ids: [elementId], transactionId: undefined }));
    });

    /**
     * Edits the table title.
     */
    const jumpToTitle = useRefCallback(() => {
        showTitle && requestAnimationFrame(() => dispatchStartEditingTitle());
    });

    /**
     * Edits the table caption.
     */
    const jumpToCaption = useRefCallback(() => {
        showCaption && requestAnimationFrame(() => dispatchStartEditingCaption());
    });

    /**
     * Starts editing the cell editor.
     */
    const startEditingCellEditor = useRefCallback((editorFocusRect?: Rect, preventInitialFocus = false): boolean => {
        const hotInstance = hotTableInstanceRef.current;
        if (!isHotInstanceAvailable(hotInstance) || isCurrentlyEditingTableCell) {
            setCellEditingSession(null);
            return false;
        }

        dispatchStartEditingTableCell({
            editorFocusRect,
            preventVirtualKeyboard: preventInitialFocus,
        });
        return true;
    });

    /**
     * Finishes editing the cell editor.
     */
    const finishEditingCellEditor = useRefCallback(() => {
        setCellEditingSession(null);

        requestAnimationFrame(() => {
            if (isCurrentlyEditingTableCell) dispatchStartEditingTableGrid();
        });
    });

    return {
        jumpToCaption,
        jumpToTitle,
        startEditingCellEditor,
        finishEditingCellEditor,
        dispatchStartEditingTableGrid,
        deselectElement,
    };
};
